import { Card, Text } from "@/design-system";
import { t } from "@/context/language";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { INTEREST_PERIODS } from "../constants/solutionInterest";
import { formatBRL } from "../constants/heroPayments";
import { InterestAreaChart } from "./interestAreaChart";

const TICK_MS = 3200;
// Antes de o card aparecer, a área fica rente à base e o valor em zero: assim
// a primeira coisa que o visitante vê é o gráfico crescendo.
const FLAT = INTEREST_PERIODS[0].points.map(() => 0);

export function SolutionInterestCard() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const seen = useInView(ref, { margin: "-80px", once: true });
  const reduceMotion = useReducedMotion();
  const revealed = seen || Boolean(reduceMotion);

  const [tick, setTick] = useState(0);
  const period = INTEREST_PERIODS[tick % INTEREST_PERIODS.length];

  const amount = useMotionValue(revealed ? period.amount : 0);
  const amountLabel = useTransform(amount, value => `+ ${formatBRL(value)}`);

  useEffect(() => {
    if (!inView || reduceMotion) return;
    const id = window.setInterval(() => setTick(prev => prev + 1), TICK_MS);
    return () => window.clearInterval(id);
  }, [inView, reduceMotion]);

  useEffect(() => {
    if (!revealed) return;
    const controls = animate(amount, period.amount, {
      duration: reduceMotion ? 0 : 1,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [revealed, reduceMotion, period.amount, amount]);

  return (
    <figure ref={ref} className="w-full max-w-md">
      <figcaption className="sr-only">
        {t.solutions.interestCard.caption}
      </figcaption>

      <Card
        className="rounded-ds-xl bg-ds-paper-marketing flex flex-col gap-4 border-transparent sm:p-6"
        aria-hidden="true"
      >
        <Text as="span" weight="semibold" className="text-ds-h3 leading-tight">
          {t.solutions.interestCard.title}
        </Text>

        <div className="grid grid-cols-[3fr_2fr] gap-3">
          <span className="rounded-ds-lg bg-ds-surface-sunken flex items-center justify-center px-2 py-3">
            <Text
              as="span"
              tone="success"
              className="font-ds-display sm:text-ds-h4 font-bold whitespace-nowrap"
            >
              <motion.span>{amountLabel}</motion.span>
            </Text>
          </span>
          <span className="rounded-ds-lg bg-ds-surface-sunken flex items-center justify-center px-2 py-3">
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={period.days}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                <Text
                  as="span"
                  tone="success"
                  className="font-ds-display sm:text-ds-h4 font-bold whitespace-nowrap"
                >
                  {period.days} {t.solutions.interestCard.days}
                </Text>
              </motion.span>
            </AnimatePresence>
          </span>
        </div>

        <InterestAreaChart points={revealed ? period.points : FLAT} />
      </Card>
    </figure>
  );
}
